import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
  BarChart,
  Bar,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  getBillingAccounts,
  getCostTrend,
  getTotalCosts,
  ApiError,
  BillingAccount,
  CostRow,
  TrendMonth,
} from '../lib/api';
import {
  CloudBadge,
  COST_CENTER_OPTIONS,
  DeltaBadge,
  ENV_OPTIONS,
  PIE_COLORS,
  SkuRow,
  featureKey,
  hkdFormat,
  lastTwelveMonths,
  prevMonth,
  rowKey,
  withMisc,
} from '../lib/costView';

export default function ServiceFeatures() {
  const months = useMemo(lastTwelveMonths, []);
  const [month, setMonth] = useState(months[0]);
  const [env, setEnv] = useState('');
  const [costCenter, setCostCenter] = useState('');
  const [accountId, setAccountId] = useState('');
  const [accounts, setAccounts] = useState<BillingAccount[]>([]);

  const [expandedFeatures, setExpandedFeatures] = useState<Set<string>>(new Set());
  const [rows, setRows] = useState<CostRow[]>([]);
  const [prevRows, setPrevRows] = useState<CostRow[]>([]);
  const [trend, setTrend] = useState<TrendMonth[]>([]);
  const [warnings, setWarnings] = useState<Record<string, string>>({});
  const [cachedAt, setCachedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getBillingAccounts()
      .then((res) => setAccounts(res.accounts))
      .catch(() => setAccounts([]));
  }, []);

  const load = useCallback(
    async (refresh = false) => {
      setLoading(true);
      setError(null);
      const common = {
        env: env || undefined,
        costCenter: costCenter || undefined,
        accountId: accountId || undefined,
        refresh,
      };
      try {
        const [res, prevRes, trendRes] = await Promise.allSettled([
          getTotalCosts({ month, ...common }),
          getTotalCosts({ month: prevMonth(month), ...common }),
          getCostTrend({ months: 12, ...common }),
        ]);
        if (res.status === 'rejected') throw res.reason;
        setRows(res.value.data);
        setWarnings(res.value.warnings);
        setCachedAt(res.value.cachedAt);
        setPrevRows(prevRes.status === 'fulfilled' ? prevRes.value.data : []);
        setTrend(trendRes.status === 'fulfilled' ? trendRes.value.months : []);
      } catch (err) {
        setError(
          err instanceof ApiError ? `${err.status}: ${err.message}` : 'Failed to load cost data'
        );
      } finally {
        setLoading(false);
      }
    },
    [month, env, costCenter, accountId]
  );

  useEffect(() => {
    load();
  }, [load]);

  const totalHkd = rows.reduce((sum, r) => sum + r.hkdCost, 0);
  const warningEntries = Object.entries(warnings);

  // Sub-US$1 SKUs bundled per cloud+feature
  const displayRows = useMemo(() => withMisc(rows, 'feature'), [rows]);

  // Feature groups sorted by subtotal desc
  const featureGroups = useMemo(() => {
    const groups = new Map<string, { rows: CostRow[]; hkdTotal: number }>();
    for (const r of displayRows) {
      const key = featureKey(r);
      const g = groups.get(key) ?? { rows: [], hkdTotal: 0 };
      g.rows.push(r);
      g.hkdTotal += r.hkdCost;
      groups.set(key, g);
    }
    return [...groups.entries()].sort((a, b) => b[1].hkdTotal - a[1].hkdTotal);
  }, [displayRows]);

  // Previous month per-row costs (for the Δ column) and per-feature totals
  const prevCostByKey = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of withMisc(prevRows, 'feature')) map.set(rowKey(r, 'feature'), r.hkdCost);
    return map;
  }, [prevRows]);

  const prevFeatureTotals = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of prevRows) {
      const key = featureKey(r);
      map.set(key, (map.get(key) ?? 0) + r.hkdCost);
    }
    return map;
  }, [prevRows]);

  const pieData = useMemo(
    () =>
      featureGroups
        .map(([name, g]) => ({ name, value: g.hkdTotal }))
        .filter((d) => d.value > 0),
    [featureGroups]
  );

  const trendData = useMemo(
    () =>
      trend.map((t) => ({
        label: new Date(`${t.month}-01T00:00:00Z`).toLocaleString('en', {
          month: 'short',
          timeZone: 'UTC',
        }),
        month: t.month,
        hkd: Math.round(t.hkdTotal),
      })),
    [trend]
  );

  const toggleFeature = (feature: string) => {
    setExpandedFeatures((prev) => {
      const next = new Set(prev);
      if (next.has(feature)) next.delete(feature);
      else next.add(feature);
      return next;
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Service Features</h1>
          <p className="text-sm text-slate-500 mt-1">
            Cross-cloud costs grouped by feature category (config/sku-mapping.yaml).
            {cachedAt && (
              <span className="ml-2 text-slate-400">
                Data as of {new Date(cachedAt).toLocaleString()}
              </span>
            )}
          </p>
        </div>
        <button
          onClick={() => load(true)}
          disabled={loading}
          className="px-4 py-2 rounded bg-brand-purple text-white text-sm hover:bg-brand-purple-light transition disabled:opacity-50"
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      <div className="flex flex-wrap gap-3">
        <select
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="border border-slate-300 rounded px-3 py-2 text-sm bg-white"
        >
          {months.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
        <select
          value={env}
          onChange={(e) => setEnv(e.target.value)}
          className="border border-slate-300 rounded px-3 py-2 text-sm bg-white"
        >
          {ENV_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          value={costCenter}
          onChange={(e) => setCostCenter(e.target.value)}
          className="border border-slate-300 rounded px-3 py-2 text-sm bg-white"
        >
          {COST_CENTER_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          value={accountId}
          onChange={(e) => setAccountId(e.target.value)}
          className="border border-slate-300 rounded px-3 py-2 text-sm bg-white"
        >
          <option value="">All accounts</option>
          {accounts.map((a) => (
            <option key={a.accountId} value={a.accountId}>
              {a.cloud.toUpperCase()} · {a.label ?? a.accountId}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 text-sm text-red-700">
          <p className="font-medium">Error</p>
          <p>{error}</p>
        </div>
      )}

      {(pieData.length > 0 || trendData.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white border border-slate-200 rounded-md p-5 shadow-sm">
            <h2 className="text-sm font-semibold text-slate-700 mb-2">
              Cost share by feature · {month}
            </h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    innerRadius="45%"
                    outerRadius="80%"
                    paddingAngle={2}
                  >
                    {pieData.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => hkdFormat.format(Number(v))} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
              {pieData.map((d, i) => (
                <li key={d.name} className="flex items-center gap-1.5">
                  <span
                    className="inline-block w-2.5 h-2.5 rounded-sm"
                    style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }}
                  />
                  {d.name}
                  <span className="text-slate-400">
                    {((d.value / pieData.reduce((s, x) => s + x.value, 0)) * 100).toFixed(1)}%
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white border border-slate-200 rounded-md p-5 shadow-sm">
            <h2 className="text-sm font-semibold text-slate-700 mb-2">
              12-month trend (HKD)
            </h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trendData} margin={{ top: 5, right: 5, bottom: 0, left: 5 }}>
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v: number) => (v >= 1000 ? `${Math.round(v / 1000)}k` : String(v))}
                    width={40}
                  />
                  <Tooltip
                    formatter={(v) => hkdFormat.format(Number(v))}
                    labelFormatter={(label, payload) => payload?.[0]?.payload?.month ?? label}
                  />
                  <Bar dataKey="hkd" radius={[3, 3, 0, 0]}>
                    {trendData.map((t) => (
                      <Cell key={t.month} fill={t.month === month ? '#0ea5e9' : '#cbd5e1'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p className="mt-2 text-xs text-slate-400">
              Highlighted bar = selected month. Bars honour the env/account filters.
            </p>
          </div>
        </div>
      )}

      {warningEntries.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4 text-sm text-amber-800">
          <p className="font-medium">Some data could not be loaded</p>
          <ul className="mt-1 list-disc list-inside">
            {warningEntries.map(([source, message]) => (
              <li key={source}>
                <span className="font-mono">{source}</span>: {message}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-4 py-3">Cloud</th>
              <th className="px-4 py-3">Service</th>
              <th className="px-4 py-3">SKU</th>
              <th className="px-4 py-3">Project</th>
              <th className="px-4 py-3">Account</th>
              <th className="px-4 py-3">Env</th>
              <th className="px-4 py-3 text-right">Native cost</th>
              <th className="px-4 py-3 text-right">HKD</th>
              <th className="px-4 py-3 text-right">Δ MoM</th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-slate-400">
                  Loading cost data…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-slate-400">
                  No cost data for the selected filters.
                </td>
              </tr>
            ) : (
              featureGroups.map(([feature, group]) => {
                const clouds = [...new Set(group.rows.map((r) => r.cloud))];
                return (
                  <Fragment key={feature}>
                    <tr
                      onClick={() => toggleFeature(feature)}
                      className="border-b border-slate-200 bg-slate-50 hover:bg-slate-100 cursor-pointer select-none"
                    >
                      <td className="px-4 py-2.5 font-medium" colSpan={6}>
                        <span className="inline-block w-4 text-slate-400">
                          {expandedFeatures.has(feature) ? '▾' : '▸'}
                        </span>
                        <span className={feature === 'Unmapped' ? 'text-amber-600' : ''}>
                          {feature}
                        </span>
                        <span className="ml-2 inline-flex gap-1">
                          {clouds.map((c) => (
                            <CloudBadge key={c} cloud={c} />
                          ))}
                        </span>
                        <span className="ml-2 text-xs text-slate-500">
                          {group.rows.length} SKU{group.rows.length === 1 ? '' : 's'}
                        </span>
                      </td>
                      <td />
                      <td className="px-4 py-2.5 text-right font-mono font-semibold whitespace-nowrap">
                        {hkdFormat.format(group.hkdTotal)}
                      </td>
                      <td className="px-4 py-2.5 text-right whitespace-nowrap">
                        <DeltaBadge
                          current={group.hkdTotal}
                          prev={prevFeatureTotals.get(feature)}
                        />
                      </td>
                    </tr>
                    {expandedFeatures.has(feature) &&
                      group.rows.map((r, i) => (
                        <SkuRow
                          key={`${r.cloud}-${r.accountId}-${r.skuId ?? r.sku}-${r.env}-${i}`}
                          row={r}
                          prevHkd={prevCostByKey.get(rowKey(r, 'feature'))}
                        />
                      ))}
                  </Fragment>
                );
              })
            )}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-slate-300 font-semibold">
                <td className="px-4 py-3" colSpan={7}>
                  Total ({rows.length} SKUs)
                </td>
                <td className="px-4 py-3 text-right font-mono whitespace-nowrap">
                  {hkdFormat.format(totalHkd)}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <DeltaBadge
                    current={totalHkd}
                    prev={prevRows.reduce((s, r) => s + r.hkdCost, 0) || undefined}
                  />
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
